import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ShieldCheck, Settings2, BarChart3, Megaphone } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
    ACCEPT_ALL,
    REJECT_ALL,
    DEFAULT_CATEGORIES,
    getStoredConsent,
    saveConsent,
    logConsent,
} from "@/lib/consent";

/**
 * CookiePreferencesModal
 * ----------------------
 * Granular consent editor. Opened by:
 *   • "Manage preferences" on the initial banner
 *   • Persistent floating launcher (CookiePreferencesLauncher)
 *   • Any "Cookie preferences" link in the footer.
 *
 * A11y:
 *   - role="dialog", aria-modal, focus is trapped by the fixed overlay + escape key.
 *   - All toggles are Radix Switches (labelled, keyboard-nav, screen-reader friendly).
 *   - Accept-all + Reject-all + Save buttons are visually identical in size/weight so
 *     no dark-pattern nudge exists between them.
 */
export default function CookiePreferencesModal({
    open,
    onClose,
    region = "OTHER",
    ccpaVisible = false,
    onDecision, // (record) => void
}) {
    const [cats, setCats] = useState(() => {
        const stored = getStoredConsent();
        return stored?.categories || DEFAULT_CATEGORIES;
    });

    useEffect(() => {
        if (!open) return;
        const stored = getStoredConsent();
        setCats(stored?.categories || DEFAULT_CATEGORIES);
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        // lock scroll
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open) return null;

    const commit = async (nextCats, source) => {
        const record = saveConsent({ categories: nextCats, region, source });
        logConsent(record); // fire-and-forget
        onDecision?.(record);
        onClose?.();
    };

    const acceptAll  = () => commit(ACCEPT_ALL, "preferences");
    const rejectAll  = () => commit(REJECT_ALL, "preferences");
    const savePicks  = () => commit({ ...DEFAULT_CATEGORIES, ...cats }, "preferences");
    const doNotSell  = () => commit(REJECT_ALL, "do-not-sell");

    return (
        <div
            className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-prefs-title"
            data-testid="cookie-preferences-modal"
        >
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
                aria-hidden="true"
            />
            <div className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-border bg-card text-foreground shadow-2xl">
                {/* Header */}
                <div className="sticky top-0 flex items-center justify-between border-b border-border bg-card/95 backdrop-blur px-5 py-4">
                    <div>
                        <div className="overline text-primary">Cookie preferences</div>
                        <h2 id="cookie-prefs-title" className="text-xl font-extrabold tracking-tight">
                            Your privacy choices
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close preferences"
                        className="p-2 rounded hover:bg-secondary transition-colors"
                        data-testid="cookie-prefs-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Intro */}
                <div className="px-5 pt-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        Melian Dialogue uses cookies to power the trading terminal, remember your
                        preferences, and measure which features actually help traders. You choose
                        what runs. Read our{" "}
                        <Link to="/legal/privacy" className="text-primary underline font-semibold">privacy policy</Link>
                        {" "}or{" "}
                        <Link to="/legal/cookies" className="text-primary underline font-semibold">cookie notice</Link>.
                    </p>
                </div>

                {/* Toggles */}
                <div className="px-5 py-4 space-y-3" data-testid="cookie-prefs-toggles">
                    <CategoryRow
                        icon={ShieldCheck}
                        title="Strictly necessary"
                        badge="Always on"
                        description="Session cookies and CSRF tokens. Without these, login and secure requests cannot work."
                        checked
                        disabled
                        testId="cat-necessary"
                    />
                    <CategoryRow
                        icon={Settings2}
                        title="Functional"
                        description="Theme (dark/light), language preference, remembered terminal layout."
                        checked={!!cats.functional}
                        onChange={(v) => setCats((c) => ({ ...c, functional: v }))}
                        testId="cat-functional"
                    />
                    <CategoryRow
                        icon={BarChart3}
                        title="Analytics"
                        description="Anonymized usage telemetry (PostHog) so we can see which features earn their keep. No selling."
                        checked={!!cats.analytics}
                        onChange={(v) => setCats((c) => ({ ...c, analytics: v }))}
                        testId="cat-analytics"
                    />
                    <CategoryRow
                        icon={Megaphone}
                        title="Marketing"
                        description="Attribution for referral campaigns and audience building. Not sold or shared with data brokers."
                        checked={!!cats.marketing}
                        onChange={(v) => setCats((c) => ({ ...c, marketing: v }))}
                        testId="cat-marketing"
                    />
                </div>

                {/* CCPA link — only rendered when the server says the visitor is US-based */}
                {ccpaVisible && (
                    <div className="px-5 pb-2">
                        <button
                            onClick={doNotSell}
                            className="text-primary underline text-sm font-semibold"
                            data-testid="cookie-do-not-sell"
                        >
                            Do Not Sell or Share My Personal Information
                        </button>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Clicking this disables all non-essential cookies and blocks any future sharing under CCPA/CPRA.
                        </p>
                    </div>
                )}

                {/* Actions — deliberately identical size & weight (no dark-pattern nudge) */}
                <div className="sticky bottom-0 border-t border-border bg-card/95 backdrop-blur px-5 py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <ActionButton
                            testId="cookie-btn-reject-all"
                            onClick={rejectAll}
                            label="Reject all"
                        />
                        <ActionButton
                            testId="cookie-btn-save"
                            onClick={savePicks}
                            label="Save preferences"
                        />
                        <ActionButton
                            testId="cookie-btn-accept-all"
                            onClick={acceptAll}
                            label="Accept all"
                        />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                        Scrolling, clicking outside this window, or navigating away is <b>not</b> consent.
                        You can change your choices anytime from the "Cookie preferences" pill in the corner.
                    </p>
                </div>
            </div>
        </div>
    );
}

function CategoryRow({ icon: Icon, title, badge, description, checked, onChange, disabled, testId }) {
    return (
        <div
            className={`rounded-xl border border-border p-4 flex items-start gap-4 ${disabled ? "bg-secondary/30" : "bg-background"}`}
            data-testid={`cookie-row-${testId}`}
        >
            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-extrabold text-sm">{title}</div>
                    {badge && <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/15 text-primary">{badge}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>
            </div>
            <div className="shrink-0 pt-1">
                <Switch
                    checked={checked}
                    onCheckedChange={onChange}
                    disabled={disabled}
                    aria-label={`Toggle ${title} cookies`}
                    data-testid={`cookie-switch-${testId}`}
                />
            </div>
        </div>
    );
}

function ActionButton({ label, onClick, testId }) {
    // Identical visual weight — mandated by "equal button prominence" rule.
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className="w-full h-11 rounded-lg border-2 border-primary bg-primary text-primary-foreground font-extrabold text-sm tracking-tight hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card"
        >
            {label}
        </button>
    );
}
